import { CREATE_SPRITE_WORKER_URL } from '../config.js';
import { fetchCache } from '../utils/fetchCache.js';
import { fetchStandaloneImageObjectUrl, revokeStandaloneImageObjectUrl } from './standaloneHdUpgrade.js';

const videoObjectUrls = new Map();

export const PAGE_LIMIT = 20;

export const standaloneVideoRequestUrl = (outputKey) => (
  `${CREATE_SPRITE_WORKER_URL}/standalone-video/${encodeURIComponent(String(outputKey).toLowerCase())}`
);

export const revokeStandaloneVideoObjectUrl = (outputKey) => {
  if (!outputKey) return;
  const key = String(outputKey).toLowerCase();
  const prev = videoObjectUrls.get(key);
  if (typeof prev === 'string' && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
  videoObjectUrls.delete(key);
};

export const fetchStandaloneVideoObjectUrl = async ({ token, outputKey, bust = false } = {}) => {
  if (!token || !outputKey) return null;
  const key = String(outputKey).toLowerCase();
  if (bust) revokeStandaloneVideoObjectUrl(key);
  const cached = videoObjectUrls.get(key);
  if (cached) return cached;
  const requestUrl = `${standaloneVideoRequestUrl(key)}${bust ? `?v=${Date.now()}` : ''}`;
  const blob = await fetchCache.fetchBlob(requestUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const objectUrl = URL.createObjectURL(blob);
  videoObjectUrls.set(key, objectUrl);
  return objectUrl;
};

export const fetchStandaloneImageRefs = async ({ token, jobId } = {}) => {
  if (!token) throw new Error('login');
  const parsedJobId = Number(jobId);
  if (!Number.isFinite(parsedJobId) || parsedJobId <= 0) {
    throw new Error('job_id is required');
  }
  return fetchCache.fetchJSON(
    `${CREATE_SPRITE_WORKER_URL}/standalone-image-refs?job_id=${encodeURIComponent(String(parsedJobId))}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      skipCache: true,
    },
  );
};

const KIND_HD = ['lowres', 'hires', 'both', 'none'];

export const fetchStandaloneMediaPage = async ({
  token,
  cursor = null,
  filter = 'both',
  hd = 'both',
  hdOnly = false,
  imagesHd = null,
  videosHd = null,
  prompt = '',
  limit = PAGE_LIMIT,
} = {}) => {
  if (!token) {
    const error = new Error('login');
    error.code = 'login';
    throw error;
  }
  const query = new URLSearchParams({
    limit: String(limit),
    filter,
  });
  const hdFilter = ['lowres', 'hires', 'both'].includes(hd) ? hd : 'both';
  if (hdFilter !== 'both') query.set('hd', hdFilter);
  if (KIND_HD.includes(imagesHd)) query.set('images_hd', imagesHd);
  if (KIND_HD.includes(videosHd)) query.set('videos_hd', videosHd);
  if (hdOnly) query.set('hd_only', '1');
  const promptFilter = String(prompt || '').trim().slice(0, 200);
  if (promptFilter) query.set('prompt', promptFilter);
  if (cursor) query.set('cursor', cursor);
  const result = await fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/standalone-media?${query.toString()}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    skipCache: true,
  });
  const items = Array.isArray(result?.items)
    ? result.items
    : (Array.isArray(result?.images) ? result.images.map((image) => ({ ...image, media_kind: 'image' })) : []);
  const totalRaw = Number(result?.totalCount);
  return {
    items,
    nextCursor: result?.nextCursor || null,
    totalCount: Number.isFinite(totalRaw) ? totalRaw : 0,
  };
};

export const fetchStandaloneMediaObjectUrl = async ({ token, item, bust = false } = {}) => {
  if (item?.media_kind === 'video') {
    return fetchStandaloneVideoObjectUrl({ token, outputKey: item.output_key, bust });
  }
  return fetchStandaloneImageObjectUrl({ token, outputKey: item.output_key, bust });
};

export const revokeStandaloneMediaObjectUrl = (item) => {
  if (item?.media_kind === 'video') revokeStandaloneVideoObjectUrl(item.output_key);
  else revokeStandaloneImageObjectUrl(item.output_key);
};

export const uploadStandaloneVideoFrame = async ({ token, blob } = {}) => {
  if (!token || !blob) throw new Error('Missing frame upload');
  return fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/upload-standalone-video-frame`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': blob.type || 'image/webp',
    },
    body: blob,
    skipCache: true,
  });
};

export const scheduleStandaloneVideo = async ({ token, body } = {}) => {
  if (!token) throw new Error('Missing schedule request');
  return fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/schedule-standalone-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body,
    skipCache: true,
  });
};

export const requestStandaloneVideoHdUpgrade = async ({ token, outputKey } = {}) => {
  if (!token || !outputKey) throw new Error('Missing upgrade request');
  return fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/upgrade-standalone-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: { output_key: outputKey },
    skipCache: true,
  });
};

export const requestStandaloneVideoDelete = async ({ token, outputKey } = {}) => {
  if (!token || !outputKey) throw new Error('Missing delete request');
  return fetchCache.fetchJSON(`${CREATE_SPRITE_WORKER_URL}/delete-standalone-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: { output_key: outputKey },
    skipCache: true,
  });
};
